import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { AuthCore } from '../core/AuthCore';

/**
 * REUSABLE user data hook - uses the /user/data endpoint
 * Returns ALL fields from public.users + email from auth.users
 * SAME PATTERN as useSimpleCredit but for ALL user data needs
 */
export const useUserData = () => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isInitialized } = useAuthStore();

  const fetchUserInfo = async () => {
    if (!isAuthenticated || !isInitialized) {
      setUserInfo(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get Supabase token
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      // Use the REUSABLE /user/data endpoint - SAME PATTERN as credits/balance
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiBaseUrl}/user/data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication expired');
        }
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to fetch user info');
      }

      // Extract user info from the response: { success: true, data: { ...userInfo } }
      const userInfoData = data.data || {};
      setUserInfo(userInfoData);

      // User data fetched successfully
    } catch (err: any) {
      console.error('User info fetch error:', err);
      setError(err.message);
      setUserInfo(null); // Fallback to null
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      fetchUserInfo();
    } else {
      setUserInfo(null);
      setError(null);
    }
  }, [isAuthenticated, isInitialized]);

  // Manual refresh function
  const refreshUserInfo = async () => {
    if (!isAuthenticated) return;
    await fetchUserInfo();
  };

  return {
    userInfo,
    isLoading,
    error,
    refreshUserInfo
  };
};
